import { graphql } from '../../schemas/server_admin_query'

const FundNavFields = graphql(`
  fragment FundNavFields on FundNAV {
    published_at
    effective_at
    cash_total_value
    assets_total_value
    valuation
    total_shares
    price_per_share
    published_by_id
    pending_shares
    cash_pending_value
  }
`)

const FeederNavFields = graphql(`
  fragment FeederNavFields on FeederNAV {
    cash_total_value
    assets_total_value
    effective_at
    total_shares
    owned_master_shares
    price_per_share
    valuation
    published_at
  }
`)

const BasicAssetFields = graphql(`
  fragment BasicAssetFields on Asset {
    id
    name
    category
    fission_ticker
  }
`)

const VaultFields = graphql(`
  fragment VaultFields on Vault {
    id
    name
    vault_address
    token_address
    token_decimals
    symbol
  }
`)

const BasicFeederFields = graphql(`
  fragment BasicFeederFields on Feeder {
    id
    name
    symbol
    market
  }
`)

const FeederFields = graphql(
  `
    fragment FeederFields on Feeder {
      ...BasicFeederFields
      fund_id
      nav_update_tolerance_percentage
      starting_price_per_share
      token_address
      token_decimals
      created_at
      updated_at
      limit_order_address
      price_band_lower_percentage
      price_band_upper_percentage
      tradable
      published
      unminted_deposit_total
      vault {
        id
        name
      }
    }
  `,
  [BasicFeederFields],
)

const FundFields = graphql(
  `
    fragment FundFields on Fund {
      id
      name
      management_fee_percentage
      fireblocks_vault_id
      management_fireblocks_vault_id
      starting_price_per_share
      created_at
      updated_at
      approve_nav_publish
      approve_nav_official
      publication_schedule
      next_publication_at
      next_publication_official
    }
  `,
  [],
)

export const query_fund_composition = graphql(`
  query query_fund_composition($fund_id: ID!, $executed_at: ISO8601DateTime) {
    fund_composition(fund_id: $fund_id, executedAt: $executed_at) {
      executed_at
      fund_id
      items {
        asset_id
        asset_name
        asset_category
        share_class
        shares_held
        spv
        spv_share_class
        total_cost
      }
    }
  }
`)

export const query_admin_fund = graphql(
  `
    query query_admin_fund($id: ID!) {
      fund(id: $id) {
        ...FundFields

        live_nav {
          ...FundNavFields
        }

        latest_published_nav {
          ...FundNavFields
        }

        latest_official_nav {
          ...FundNavFields
        }

        upcoming_nav {
          ...FundNavFields
        }

        feeders {
          ...FeederFields

          vault {
            ...VaultFields
          }

          live_nav {
            ...FeederNavFields
          }
        }
      }
    }
  `,
  [FeederFields, FeederNavFields, FundNavFields, FundFields, VaultFields],
)

/** a range of fund-level navs for a fund */
export const query_admin_fund_navs = graphql(
  `
    query query_admin_fund_navs(
      $id: ID!
      $count: Int!
      $start_date: ISO8601Date
      $end_date: ISO8601Date
    ) {
      fund(id: $id) {
        id
        name
        starting_price_per_share
        navs(first: $count, start_date: $start_date, end_date: $end_date) {
          nodes {
            ...FundNavFields
          }
        }
        feeders {
          ...BasicFeederFields
          navs(first: $count, start_date: $start_date, end_date: $end_date) {
            nodes {
              ...FeederNavFields
            }
          }
        }
      }
    }
  `,
  [BasicFeederFields, FeederNavFields, FundNavFields],
)

/** a range of company-level navs for a fund (new nav structure) */
export const query_admin_fund_asset_navs = graphql(
  `
    query query_admin_fund_asset_navs(
      $id: ID!
      $count: Int
      $start_date: ISO8601Date
      $end_date: ISO8601Date
      $effective_at: ISO8601DateTime
    ) {
      fund(id: $id) {
        assets {
          nodes {
            asset {
              ...BasicAssetFields
            }
            navs(
              first: $count
              start_date: $start_date
              end_date: $end_date
              effective_at: $effective_at
            ) {
              nodes {
                published_at
                effective_at
                shares_held
                shares_total_cost
                shares_value_held
              }
            }
          }
          total_count
        }
      }
    }
  `,
  [BasicAssetFields],
)

export const query_admin_fund_transactions = graphql(`
  query query_admin_fund_transactions(
    $fund_id: ID!
    $limit: Int!
    $after: String
  ) {
    fund(id: $fund_id) {
      transactions(first: $limit, after: $after) {
        nodes {
          id
          executed_at
          action
          dollars
          master_shares
          action_blockchain_tx
          action_external_tx
          action_external_type
          mint_blockchain_amount
          mint_blockchain_number
          user_wallet_address
          feeder_id
          fund_id
          created_at
          updated_at
          asset_transaction {
            id
            executed_at
            price_per_share
            share_count
            side
            spv
            spv_share_class
            share_class
            fees
            total_price
            created_at
            updated_at
          }
        }
        total_count
      }
    }
  }
`)

export const query_admin_fund_assets = graphql(
  `
    query query_admin_fund_assets($fund_id: ID!) {
      assets {
        nodes {
          ...BasicAssetFields
        }
      }
      fund(id: $fund_id) {
        assets {
          nodes {
            id
            notable
            asset {
              ...BasicAssetFields
            }
            live_nav {
              shares_held
              shares_value_held
              shares_total_cost
              effective_at
              published_at
              official
            }
            latest_official_nav {
              shares_held
              shares_value_held
              shares_total_cost
              effective_at
              published_at
              official
            }
            latest_published_nav {
              shares_held
              shares_value_held
              shares_total_cost
              effective_at
              published_at
              official
            }
          }
        }
      }
    }
  `,
  [BasicAssetFields],
)

export const query_admin_assets = graphql(
  `
    query query_admin_assets {
      assets {
        nodes {
          ...BasicAssetFields
        }
      }
    }
  `,
  [BasicAssetFields],
)

export const mutation_create_fund_deposit_transaction = graphql(`
  mutation create_fund_deposit_transaction(
    $feeder_id: ID!
    $amount: Float!
    $user_wallet_address: String!
    $action_external_tx: String!
    $action_external_type: String!
    $date: ISO8601Date
  ) {
    create_fund_deposit_transaction(
      feeder_id: $feeder_id
      amount: $amount
      user_wallet_address: $user_wallet_address
      action_external_tx: $action_external_tx
      action_external_type: $action_external_type
      date: $date
    ) {
      fund_transaction {
        id
        date
        action
        dollars
        action_external_tx
        action_external_type
        user_wallet_address
        feeder_id
        fund_id
      }
      errors
    }
  }
`)

export const query_admin_funds = graphql(
  `
    query query_admin_funds {
      funds {
        nodes {
          ...FundFields

          live_nav {
            ...FundNavFields
          }

          feeders {
            ...FeederFields
          }
        }
        total_count
      }
    }
  `,
  [FundFields, FundNavFields, FeederFields],
)

const AssetPriceFields = graphql(`
  fragment AssetPriceFields on AssetPrice {
    date
    price_per_share
    source
  }
`)

export const query_asset_prices = graphql(
  `
    query query_asset_prices(
      $first: Int
      $last: Int
      $after: String
      $before: String
      $start_date: ISO8601Date
      $end_date: ISO8601Date
      $category: String
    ) {
      asset_prices(
        first: $first
        last: $last
        after: $after
        before: $before
        start_date: $start_date
        end_date: $end_date
        category: $category
      ) {
        nodes {
          ...AssetPriceFields
          asset {
            id
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        total_count
      }
    }
  `,
  [AssetPriceFields],
)

export const mutation_approve_fund_nav = graphql(`
  mutation approve_fund_nav(
    $fund_id: ID!
    $effective_at: ISO8601DateTime!
    $overwrite: Boolean
  ) {
    approve_fund_nav(
      fund_id: $fund_id
      effective_at: $effective_at
      overwrite: $overwrite
    ) {
      fund_nav {
        published_at
        effective_at
        cash_total_value
        assets_total_value
        valuation
        total_shares
        price_per_share
      }
      errors
    }
  }
`)

export const mutation_update_fund_asset_notable = graphql(`
  mutation update_fund_asset_notable(
    $fund_id: ID!
    $asset_id: ID!
    $notable: Boolean!
  ) {
    update_fund_asset_notable(
      fund_id: $fund_id
      asset_id: $asset_id
      notable: $notable
    ) {
      errors
      fund_asset {
        id
        notable
        asset {
          id
          name
        }
      }
    }
  }
`)
